import { useEffect, useRef, useState } from "react";
import { Color, Scene, Fog, PerspectiveCamera, Vector3 } from "three";
import ThreeGlobe from "three-globe";
import { useThree, Canvas, extend } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import countries from "../../data/globe.json";

extend({ ThreeGlobe });

const RING_PROPAGATION_SPEED = 3;
const aspect = 1.2;
const cameraZ = 300;

let numbersOfRings = [0];

export function hexToRgb(hex) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

export function genRandomNumbers(min, max, count) {
  const arr = [];
  while (arr.length < count) {
    const r = Math.floor(Math.random() * (max - min)) + min;
    if (arr.indexOf(r) === -1) arr.push(r);
  }
  return arr;
}

export function Globe({ globeConfig, data }) {
  const [globeData, setGlobeData] = useState(null);
  const globeRef = useRef(null);

  const defaultProps = {
    pointSize: 1,
    atmosphereColor: "#ffffff",
    showAtmosphere: true,
    atmosphereAltitude: 0.1,
    polygonColor: "rgba(70,80,95,0.85)",
    globeColor: "#d8e8f0",
    globeOpacity: 0.28,
    emissive: "#e8f4f8",
    emissiveIntensity: 0.15,
    shininess: 0.6,
    hexPolygonResolution: 3,
    hexPolygonMargin: 0.78,
    arcTime: 2000,
    arcLength: 0.9,
    rings: 1,
    maxRings: 3,
    ...globeConfig,
  };

  useEffect(() => {
    if (globeRef.current && data && data.length > 0) {
      _buildData();
      _buildMaterial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const _buildMaterial = () => {
    if (!globeRef.current) return;
    const globeMaterial = globeRef.current.globeMaterial();
    const opacity =
      typeof defaultProps.globeOpacity === "number"
        ? defaultProps.globeOpacity
        : 0.28;

    globeMaterial.color = new Color(defaultProps.globeColor);
    globeMaterial.emissive = new Color(defaultProps.emissive);
    globeMaterial.emissiveIntensity = defaultProps.emissiveIntensity || 0.15;
    globeMaterial.shininess = defaultProps.shininess || 0.6;
    globeMaterial.transparent = true;
    globeMaterial.opacity = opacity;
    globeMaterial.depthWrite = opacity >= 0.95;
  };

  const _buildData = () => {
    if (!data || data.length === 0) return;
    const points = [];
    for (let i = 0; i < data.length; i++) {
      const arc = data[i];
      if (
        !arc ||
        typeof arc.startLat !== "number" ||
        typeof arc.startLng !== "number" ||
        typeof arc.endLat !== "number" ||
        typeof arc.endLng !== "number"
      ) {
        continue;
      }
      const rgb = hexToRgb(arc.color);
      if (!rgb) continue;

      points.push({
        size: defaultProps.pointSize,
        order: arc.order,
        color: (t) => `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${1 - t})`,
        lat: arc.startLat,
        lng: arc.startLng,
      });
      points.push({
        size: defaultProps.pointSize,
        order: arc.order,
        color: (t) => `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${1 - t})`,
        lat: arc.endLat,
        lng: arc.endLng,
      });
    }

    const filteredPoints = points.filter(
      (v, i, a) =>
        a.findIndex((v2) => ["lat", "lng"].every((k) => v2[k] === v[k])) === i
    );
    setGlobeData(filteredPoints);
  };

  useEffect(() => {
    if (globeRef.current && globeData) {
      globeRef.current
        .hexPolygonsData(countries.features)
        .hexPolygonResolution(defaultProps.hexPolygonResolution ?? 3)
        .hexPolygonMargin(defaultProps.hexPolygonMargin ?? 0.78)
        .showAtmosphere(defaultProps.showAtmosphere)
        .atmosphereColor(defaultProps.atmosphereColor)
        .atmosphereAltitude(defaultProps.atmosphereAltitude)
        .hexPolygonColor(() => defaultProps.polygonColor);
      startAnimation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globeData]);

  const startAnimation = () => {
    if (!globeRef.current || !globeData || !data || data.length === 0) return;

    const validArcs = data.filter(
      (arc) =>
        arc &&
        typeof arc.startLat === "number" &&
        !isNaN(arc.startLat) &&
        typeof arc.startLng === "number" &&
        !isNaN(arc.startLng) &&
        typeof arc.endLat === "number" &&
        !isNaN(arc.endLat) &&
        typeof arc.endLng === "number" &&
        !isNaN(arc.endLng) &&
        typeof arc.arcAlt === "number" &&
        !isNaN(arc.arcAlt)
    );

    if (validArcs.length === 0) return;

    globeRef.current
      .arcsData(validArcs)
      .arcStartLat((d) => d.startLat)
      .arcStartLng((d) => d.startLng)
      .arcEndLat((d) => d.endLat)
      .arcEndLng((d) => d.endLng)
      .arcColor((e) => e.color)
      .arcAltitude((e) => e.arcAlt)
      .arcStroke(() => [0.32, 0.28, 0.3][Math.round(Math.random() * 2)])
      .arcDashLength(defaultProps.arcLength)
      .arcDashInitialGap((e) => e.order)
      .arcDashGap(15)
      .arcDashAnimateTime(() => defaultProps.arcTime);

    globeRef.current
      .pointsData(globeData)
      .pointColor((e) =>
        typeof e.color === "function" ? e.color(0) : e.color
      )
      .pointsMerge(true)
      .pointAltitude(0.0)
      .pointRadius(2);

    globeRef.current
      .ringsData([])
      .ringColor((e) => (t) => e.color(t))
      .ringMaxRadius(defaultProps.maxRings)
      .ringPropagationSpeed(RING_PROPAGATION_SPEED)
      .ringRepeatPeriod(
        (defaultProps.arcTime * defaultProps.arcLength) / defaultProps.rings
      );
  };

  useEffect(() => {
    if (!globeRef.current || !globeData || !data || data.length === 0) return;

    const interval = setInterval(() => {
      if (!globeRef.current || !globeData) return;
      numbersOfRings = genRandomNumbers(
        0,
        data.length,
        Math.floor((data.length * 4) / 5)
      );
      const validRingsData = globeData.filter((d, i) => {
        if (!numbersOfRings.includes(i)) return false;
        return (
          typeof d.lat === "number" &&
          !isNaN(d.lat) &&
          typeof d.lng === "number" &&
          !isNaN(d.lng)
        );
      });
      if (validRingsData.length > 0) {
        globeRef.current.ringsData(validRingsData);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [globeData, data]);

  return <threeGlobe ref={globeRef} />;
}

export function WebGLRendererConfig() {
  const { gl, size } = useThree();

  useEffect(() => {
    gl.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    gl.setSize(size.width, size.height);
    gl.setClearColor(0xffaaff, 0);
  }, [gl, size]);

  return null;
}

export function World(props) {
  const { globeConfig } = props;
  const scene = new Scene();
  scene.fog = new Fog(0xf5fafc, 800, 2500);

  return (
    <Canvas scene={scene} camera={new PerspectiveCamera(50, aspect, 180, 1800)}>
      <WebGLRendererConfig />
      <ambientLight
        color={globeConfig.ambientLight || "#ffffff"}
        intensity={1.1}
      />
      <directionalLight
        color={globeConfig.directionalLeftLight || "#ffffff"}
        position={new Vector3(-400, 100, 400)}
        intensity={0.85}
      />
      <directionalLight
        color={globeConfig.directionalTopLight || "#ffffff"}
        position={new Vector3(-200, 500, 200)}
        intensity={0.7}
      />
      <pointLight
        color={globeConfig.pointLight || "#ffffff"}
        position={new Vector3(-200, 500, 200)}
        intensity={0.55}
      />
      <Globe {...props} />
      <OrbitControls
        enablePan={false}
        enableZoom={false}
        minDistance={cameraZ}
        maxDistance={cameraZ}
        autoRotateSpeed={globeConfig.autoRotateSpeed ?? 0.5}
        autoRotate={globeConfig.autoRotate !== false}
        minPolarAngle={Math.PI / 3.5}
        maxPolarAngle={Math.PI - Math.PI / 3}
      />
    </Canvas>
  );
}
